import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, MessageCircle, Globe, Lock, Camera } from 'lucide-react';
import PhotoUpload from './PhotoUpload';
import PhotoLightbox from './PhotoLightbox';
import { Avatar } from './Avatar';
import { ReactionBar } from './Reactions';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';

const Chat = ({ session }) => {
  const { user } = useAuth();
  const { sendMessage, socket } = useSocket();
  const [message, setMessage] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [messages, setMessages] = useState([]);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing messages from session
  useEffect(() => {
    if (session.messages) {
      const messageList = Object.values(session.messages);
      setMessages(messageList.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    }
  }, [session.messages]);

  // Subscribe to live socket messages
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (data) => {
      setMessages((prev) => {
        // Check for duplicate message IDs
        const exists = prev.some(msg => msg.id === data.id);
        if (exists) return prev;
        
        const next = [...prev, data];
        return next.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      });
    };

    socket.on('messageReceived', handleMessage);
    return () => {
      socket.off('messageReceived', handleMessage);
    };
  }, [socket]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!message.trim()) return;

    sendMessage(message.trim(), isVisible);
    setMessage('');
  };

  const handleToggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="card h-[62vh] sm:h-[30rem] max-h-[640px] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center">
          <MessageCircle className="w-5 h-5 mr-2" />
          Chat
        </h2>
        <button
          onClick={handleToggleVisibility}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isVisible
              ? 'bg-gray-600 text-gray-300 hover:text-white'
              : 'bg-amber-600/30 text-amber-300'
          }`}
          title={isVisible ? 'Messages visible to everyone' : 'Private — only you and the host can see'}
          aria-label={isVisible ? 'Messages are public. Switch to private.' : 'Messages are private to the host. Switch to public.'}
        >
          {isVisible ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          {isVisible ? 'Public' : 'Host only'}
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No messages yet</p>
            <p className="text-gray-400 text-sm">Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-end gap-2 ${msg.sender?.uid === (user?.uid) ? 'justify-end' : 'justify-start'}`}
            >
              {msg.sender?.uid !== user?.uid && (
                <Avatar
                  uid={msg.sender?.uid}
                  name={msg.sender?.displayName || msg.senderName}
                  size="sm"
                  className="mb-0.5"
                />
              )}
              <div className={`max-w-[75%] sm:max-w-xs lg:max-w-md ${
                msg.sender?.uid === (user?.uid)
                  ? 'jh-gradient text-white'
                  : 'bg-gray-700 text-white'
              } rounded-2xl px-3 py-2`}>
                <div className="flex items-center space-x-2 mb-1 flex-wrap">
                  <span className="text-xs font-medium">
                    {msg.sender?.displayName || msg.senderName || 'Unknown'}
                  </span>
                  <span className="text-xs opacity-70">
                    {formatTime(msg.timestamp)}
                  </span>
                  {!msg.isVisible && (
                    <Lock className="w-3 h-3 opacity-70" title="Only you and the host can see this" />
                  )}
                </div>
                {msg.type === 'photo' ? (
                  <div>
                    <img
                      src={msg.photoUrl}
                      alt={msg.caption || 'Shared photo'}
                      className="max-w-[200px] rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxPhoto({ url: msg.photoUrl, caption: msg.caption, senderName: msg.sender?.displayName, timestamp: msg.timestamp })}
                    />
                    {msg.caption && <p className="text-sm mt-1 break-words" style={{ overflowWrap: 'anywhere' }}>{msg.caption}</p>}
                  </div>
                ) : (
                  <p className="text-sm break-words" style={{ overflowWrap: 'anywhere' }}>{msg.message}</p>
                )}
              </div>
            </motion.div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick reactions — the party sees them float up everywhere */}
      <ReactionBar size="sm" className="mb-3" />

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="flex space-x-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`Type a message${!isVisible ? ' (only host sees it)' : ''}...`}
          className="input-field flex-1"
          maxLength={500}
        />
        <button
          type="button"
          onClick={() => setShowPhotoUpload(true)}
          className="btn-secondary p-2"
          title="Share photo"
        >
          <Camera className="w-4 h-4" />
        </button>
        <button
          type="submit"
          disabled={!message.trim()}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
      {showPhotoUpload && <PhotoUpload onClose={() => setShowPhotoUpload(false)} />}
      {lightboxPhoto && <PhotoLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />}
    </div>
  );
};

export default Chat; 